import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    useLoginMutation,
    useActiveDirectoryLoginMutation,
    useLazyGetUserQuery,
    useLazyGetActuatorInfoQuery,
    useLazyIdpProviderLoginQuery,
    useSetDefaultUserOrgMutation,
} from '../../api';

import { getProjectIdByOrgName, getFirstEligibleProjectId } from '../../lib/loginHelpers';

import { RV_TOOLS_UPLOAD_SIZE_LIMIT } from '../../constants/discovery';

import {
    getUserRole,
    getUrlParam,
    getUserFullName,
    handleError,
    getStorageVar,
    removeStorageVar,
    getSessionVar,
    setSessionVar,
} from '../../lib/helpers';

import WOWO from '../../lib/wowo';
import { handleLoginError } from '../../lib/handleLoginError';
import { useNotificationContext } from '../../providers/NotificationProvider';

import { LoginForm } from './LoginForm';
import { Form } from '../../components';
import { MFAModal } from '../../components/modals/MFAModal';
import { ResetOneTimePasswordModal } from '../../components/modals/ResetOneTimePasswordModal';
import { MFASetupModal } from '../../components/modals/MFASetupModal';

export const LoginWrapper = () => {
    const [showMFAModal, setShowMFAModal] = useState(false);
    const [showMFASetupModal, setShowMFASetupModal] = useState(false);
    const [showResetPasswordCodeModal, setShowResetPasswordCodeModal] = useState(false);
    const [showResetOneTimePasswordModal, setShowResetOneTimePasswordModal] = useState(false);
    const [credentials, setCredentials] = useState({ email: '', password: '' });

    const [form] = Form.useForm();
    const { notifySuccess, notifyError } = useNotificationContext();
    const navigate = useNavigate();

    const [login, { data, isLoading }] = useLoginMutation();
    const [activeDirectoryLogin, { data: adData, isLoading: adLoading }] = useActiveDirectoryLoginMutation();
    const [setDefaultUserOrg] = useSetDefaultUserOrgMutation();

    // use lazy queries for requests that should be triggered on user action
    // and not on component mount
    // https://redux-toolkit.js.org/rtk-query/api/created-api/hooks#uselazyquery
    const [triggerGetIdpProviderUser] = useLazyIdpProviderLoginQuery();
    const [triggerGetUser] = useLazyGetUserQuery();
    const [triggerGetVersionInfo] = useLazyGetActuatorInfoQuery();

    // check for user id and token on url param when component mounts
    // if present, set these session parameters
    useEffect(() => {
        const userId = getUrlParam('user_id');

        if (userId) setSessionParametersOnLogin(userId, null);
    }, []);

    const extendOrganizationWithUserRole = (organizations) => {
        return organizations.map((org) => {
            const hasSiblingAdmin = organizations.some(
                (testOrg) =>
                    testOrg.parent_organization_id === org.parent_organization_id && testOrg.role === 'ORG_ADMIN',
            );
            return {
                ...org,
                ...(hasSiblingAdmin ? { role: 'ORG_ADMIN' } : {}),
            };
        });
    };

    const handleMFALogin = async ({ mfaCode }) => {
        handleSignInRequest(credentials.email, credentials.password, mfaCode);
    };

    const handleRiverMeadowLogin = async () => {
        const { email, password } = await form.getFieldsValue();
        setCredentials({ email, password });
        handleSignInRequest(email, password);
    };

    const handleSignInRequest = async (email = null, password = null, mfaCode = null) => {
        // Step 1: Make initial login request, get userId off response
        try {
            const loginResponse = await login({ email, password, mfaCode }).unwrap();
            const userId = loginResponse.user_id;
            // Step 2: Set session storage values if login was successful
            setSessionParametersOnLogin(userId);
        } catch ({ data }) {
            let { message, resolution } = data;
            handleLoginError({
                error: data,
                actions: {
                    showMFAModal: () => setShowMFAModal(true),
                    hideMFAModal: () =>
                        notifyError({
                            description: 'Verificaiton code provided was invalid',
                            onClose: setShowMFAModal(false),
                        }),
                    setupMFA: () => {
                        notifyError({
                            description:
                                'Your company requires you to set up Two-Factor Authentication, you will now be redirected to the Two-Factor Authentication setup form. Please setup your preferred MFA method',
                            onClose: setShowMFASetupModal(true),
                        });
                    },
                    mfaFailedToVerify: () => notifyError({ description: 'MFA code provided was invalid' }),
                    credentialsExpired: () => {
                        notifyError({
                            description:
                                'Your password expired, you will now be redirected to the password reset form.',
                            onClose: setShowForgotPassword(true),
                        });
                    },
                    invalidCredentials: () => notifyError({ message: 'Invalid Credentials' }),
                    externalAuthEnabled: () => notifyError({ message: 'External Authentication Enabled' }),
                    accountLocked: () =>
                        notifyError({
                            message: 'User Account Locked',
                            description:
                                'Your account has been locked. If this is unexpected, please contact your system administrator.',
                            onClose: setShowResetPasswordCodeModal(true),
                        }),
                    defaultAction: () =>
                        notifyError({
                            message: message || 'Login failed',
                            description: resolution || 'An unknown error occurred.',
                        }),
                },
            });
        }
    };

    const handleAlternateLogin = async () => {
        const values = await form.getFieldsValue();
        const { email, password } = values;
        // Set the provider based on the presence of email or password
        let provider = email || password ? 'active_directory' : 'azure';

        if (provider === 'active_directory') {
            handleActiveDirectoryLogin(email, password);
        } else {
            handleIdpProviderLogin(provider);
        }
    };

    const handleActiveDirectoryLogin = async (email, password) => {
        try {
            const loginResponse = await activeDirectoryLogin({ username: email, password }).unwrap();
            const userId = loginResponse.user_id;
            setSessionParametersOnLogin(userId);
        } catch (error) {
            handleError(error, notifyError, 'Authentication Failed');
        }
    };

    const handleIdpProviderLogin = async (provider) => {
        try {
            const url = await triggerGetIdpProviderUser({ provider }).unwrap();
            document.location.href = url;
        } catch ({ data }) {
            notifyError({ messsage: data.message || 'Authentication failed' });
        }
    };

    const handleGoogleLogin = async () => {
        handleIdpProviderLogin('google');
    };

    // Step 2: Set session storage values after successful login
    const setSessionParametersOnLogin = async (userId, orgName = null) => {
        try {
            // Set initial session storage values if userId is present
            if (userId) {
                sessionStorage.setItem('authState', 'authenticated');
            }
            let mode = getUrlParam('mode');
            sessionStorage.setItem('mode', mode);
            setSessionVar('displayWelcomeModal', true);

            // Get user record using userId
            const userRecord = await triggerGetUser({ id: userId }).unwrap();

            let userOrganizations = extendOrganizationWithUserRole(userRecord.organizations);

            // Set additional session storage values based on userRecord
            saveLoginSessionItems(userRecord, userOrganizations);

            if (getUserRole() === 'VIEWER') {
                // We want to redirect viewers to View Migrations page, but only once when they logged in,
                // as Home page would also stay available for them
                setSessionVar('viewer_redirect_to_migrations', true);
            }

            if (orgName) {
                getUserOrganizations(orgName, userRecord, userId);
            } else {
                initializeUserOrganization(userRecord, userId);
            }
        } catch (error) {
            notifyError({ description: 'Login failed' });
        }
    };

    // Step 3: Fetch the version info
    const loadVersionInfo = async (userRecord) => {
        if (userRecord.is_one_time_password) {
            setShowResetOneTimePasswordModal(true);
            return;
        }

        try {
            // Version info request
            const versionInfo = await triggerGetVersionInfo().unwrap();
            let sdmEnabledClouds = versionInfo.sdm_enabled_clouds ? versionInfo.sdm_enabled_clouds : [];
            let destination = '/home';
            let applianceLatestVersion = versionInfo.appliance_latest_version || '';
            setSessionVar('applianceLatestVersion', applianceLatestVersion);
            setSessionVar('sdmEnabledClouds', sdmEnabledClouds);
            setSessionVar('sdm_clouds_enabled', sdmEnabledClouds);
            setSessionVar('proxy', versionInfo.proxy);
            WOWO.applyServerFlags(versionInfo?.wowo || {});

            //Appliance URL mappings for each environment.
            const mappingForRepoUrls = {
                'https://migrate.rivermeadow.com':
                    'https://rm-files.rivermeadow.com/rivermeadow-packages/95a8536f-38cd-48a6-9a2e-a872cc5bf49d/appliances',
                'https://migrate-eu.rivermeadow.com':
                    'https://rm-files-eu.rivermeadow.com/rivermeadow-packages-eu/95a8536f-38cd-48a6-9a2e-a872cc5bf49d/appliances',
                'https://migrate.rivermeadow.eu': 'https://migrate.rivermeadow.eu/repo/appliances',
            };
            //Default appliance URL, used when no URL is found in the mapping or when running the UI locally.
            const appliancesDefaultUrl =
                'https://rm-files.rivermeadow.com/rivermeadow-packages/95a8536f-38cd-48a6-9a2e-a872cc5bf49d/appliances';
            const repo_urls = {
                appliances: mappingForRepoUrls[window.location.origin] || appliancesDefaultUrl,
            };
            setSessionVar('repo_urls', versionInfo.repo_urls || repo_urls);
            setSessionVar('allow_logs_download', versionInfo.allow_logs_download);
            // TODO: remove size placeholders once we get the value from the FE
            setSessionVar(
                'discovery_multipart_compressed_max_file_size_in_megabytes',
                versionInfo['discovery_multipart_compressed_max_file_size_in_megabytes'] ||
                    RV_TOOLS_UPLOAD_SIZE_LIMIT.compressedMB,
            );
            setSessionVar(
                'discovery_multipart_uncompressed_max_file_size_in_megabytes',
                versionInfo['discovery_multipart_uncompressed_max_file_size_in_megabytes'] ||
                    RV_TOOLS_UPLOAD_SIZE_LIMIT.uncompressedMB,
            );

            WOWO.setConfigurationParameter('dm_expiration_days', versionInfo.dm_expiration_days);
            WOWO.setConfigurationParameter(
                'windows_full_migration_transfer_type',
                versionInfo.windows_full_migration_transfer_type,
            );
            if (versionInfo?.wowo?.mode_env) {
                WOWO.enableModeEnv(versionInfo.wowo.mode_env);
            }
            if (!!versionInfo.private_deployment) {
                setSessionVar('private_deployment', versionInfo.private_deployment);
            }

            // Step 4: Redirect to home page
            let targetUrl = getStorageVar('target-url');
            if (targetUrl) {
                destination = targetUrl;
                removeStorageVar('target-url');
            }
            navigate(destination);
        } catch (err) {}
    };

    const saveLoginSessionItems = (userRecord, userOrganizations) => {
        sessionStorage.setItem('userOrg', userRecord.organization);
        setSessionVar('organizations', userOrganizations);
        setSessionVar('user', userRecord); // Compare this with the values in Auto2.  Why are we storing both response and userRecord in session when they are the same thing?
        sessionStorage.setItem('userRole', userRecord.role);
        sessionStorage.setItem('systemRole', userRecord.system_role);
        setSessionVar('userRecord', userRecord);
        sessionStorage.setItem('loggedInUserId', userRecord.id);
        sessionStorage.setItem('userEmail', userRecord.email);
    };

    const getUserOrganizations = async (orgName, userRecord, userId) => {
        let orgID = getProjectIdByOrgName(userRecord.organizations, orgName);
        if (!orgID) {
            notifyError({
                message: 'Invalid Organization',
                description: `Could not find Organization: ${orgName} for this account. Please enter a valid Organization name.`,
                onClose: () => window.location.reload(),
            });
        } else {
            if (orgID !== userRecord.organization) {
                try {
                    await setDefaultUserOrg({ userId, orgId: orgID });
                    sessionStorage.setItem('userOrg', orgID);
                    let user = getSessionVar('user');
                    user.organization = orgID;
                    setSessionVar('user', user);
                    loadVersionInfo(userRecord);
                } catch (error) {
                    let orgText = orgName ? `into Organization: ${orgName}` : 'in';
                    notifyError({
                        message: 'Error',
                        description: `We were not able to log you (${orgText}). Please try again.`,
                        onClose: () => window.location.reload(),
                    });
                }
            } else {
                loadVersionInfo(userRecord);
            }
        }
    };

    const initializeUserOrganization = async (userRecord, userId) => {
        let userOrgPresent = userRecord.organizations.filter((obj) => obj.id === userRecord.organization).length > 0;

        if (userOrgPresent) {
            loadVersionInfo(userRecord);
        } else {
            let orgID = getFirstEligibleProjectId(userRecord.organizations);
            if (!orgID) {
                notifyError({
                    message:
                        'Your account is not assigned to any Organization. Please contact your organization admin.',
                    onClose: () => window.location.reload(),
                });
            } else {
                try {
                    await setDefaultUserOrg({ userId, orgId: orgID });
                } catch (error) {
                    notifyError({
                        message: 'Error',
                        description: `We were not able to log you into an organization`,
                        onClose: () => window.location.reload(),
                    });
                }
            }
        }
    };

    return (
        <>
            <LoginForm
                form={form}
                isLoading={isLoading}
                handleAlternateLogin={handleAlternateLogin}
                handleGoogleLogin={handleGoogleLogin}
                onFinish={handleRiverMeadowLogin}
                showResetPasswordCodeModal={showResetPasswordCodeModal}
                setShowResetPasswordCodeModal={setShowResetPasswordCodeModal}
            />
            <MFAModal
                open={showMFAModal}
                onCancel={() => setShowMFAModal(false)}
                isLoading={isLoading}
                onFinish={handleMFALogin}
            />
            <ResetOneTimePasswordModal
                open={showResetOneTimePasswordModal}
                onCancel={() => setShowResetOneTimePasswordModal(false)}
            />
            <MFASetupModal
                open={showMFASetupModal}
                onCancel={() => setShowMFASetupModal(false)}
                email={credentials.email}
                password={credentials.password}
            />
        </>
    );
};
